import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCircle2, Loader2, LayoutDashboard, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { TURNSTILE_SITE_KEY } from "@/lib/turnstile";

interface ContributorLoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "email" | "sent";

export function ContributorLoginModal({
  open,
  onOpenChange,
}: ContributorLoginModalProps) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  const handleSendMagicLink = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    if (!captchaToken) {
      toast.error("Please complete the security check");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-contributor-magic-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            email: normalizedEmail,
            captchaToken 
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        // Reset captcha on error so user can try again
        turnstileRef.current?.reset();
        setCaptchaToken(null);
        throw new Error(result.error || "Failed to send magic link");
      }

      setEmail(normalizedEmail);
      setStep("sent");
    } catch (error: any) {
      toast.error(error.message || "Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after modal closes
    setTimeout(() => {
      setStep("email");
      setEmail("");
      setCaptchaToken(null);
      turnstileRef.current?.reset();
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {step === "email" && (
          <>
            <DialogHeader>
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <LayoutDashboard className="w-6 h-6 text-primary" />
              </div>
              <DialogTitle className="text-center">Access Your Dashboard</DialogTitle>
              <DialogDescription className="text-center">
                Enter your email to receive a login link
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="contributor-email">Email address</Label>
                <Input
                  id="contributor-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && captchaToken && handleSendMagicLink()}
                />
              </div>
              
              {/* Cloudflare Turnstile CAPTCHA */}
              <div className="flex min-h-[65px] justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => {
                    setCaptchaToken(token);
                    setCaptchaError(null);
                  }}
                  onError={(err) => {
                    setCaptchaToken(null);
                    const msg = `Security check failed (${err || "unknown"}). Please refresh the page.`;
                    setCaptchaError(msg);
                    toast.error(msg);
                  }}
                  onExpire={() => {
                    setCaptchaToken(null);
                  }}
                  options={{
                    theme: "auto",
                    size: "normal",
                    appearance: "always",
                    execution: "render",
                  }}
                />
              </div>
              {captchaError && (
                <div className="flex items-start gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{captchaError}</span>
                </div>
              )}

              <Button
                onClick={handleSendMagicLink}
                disabled={loading || !captchaToken}
                className="w-full"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                {loading ? "Sending..." : "Send Magic Link"}
              </Button>
            </div>
          </>
        )}

        {step === "sent" && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2">
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <DialogTitle>Check Your Email</DialogTitle>
              <DialogDescription className="mt-2">
                Check your email — we sent you a sign-in link.
                <br />
                <span className="font-medium text-foreground">{email}</span>
                <br />
                <span className="text-xs mt-2 block">
                  The link expires in 1 hour.
                </span>
                <span className="text-xs mt-1 block">
                  If you don't see it, check your spam folder.
                </span>
              </DialogDescription>
            </DialogHeader>
            <Button onClick={handleClose} className="w-full mt-4">
              Done
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
